import React, { useCallback, useMemo, useRef, useState } from 'react'
import './ImageDecorator.less'
import Image from '../Image';
import { Resizable } from 're-resizable';
import { MASKS_TYPE, defaultImgAspectRatio, roundButtonWidth, viewerTypes } from '../../constants/constants';
import { getRatioMove, reinitializeBoxWidthAndHeight, rotatedBoxDelta, } from '../../utils/boxes';
import { useEditor } from '../../contexts/EditorContext';
import ReactDOM from 'react-dom';
import FloatyImageMenu from './FloatyImageMenu';
import { isAnUrl, useOutsideClickHandler } from '../../utils/browser';
import { useStore } from '../../store/store';
import { usePreviewContext } from '../../contexts/PreviewContext';

const ImageUpdater = ({ simulatedBox, entityKey, menuAncestorType, menuAncestorIds, editorState }) => {
  const header = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.HEADER.id))
  const footer = useStore(({ masks }) => masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id))
  const fromPdf = useStore(({ fromPdf }) => fromPdf)
  const { fromViewer } = usePreviewContext();

  const ref = useRef()
  const [width, setWidth] = useState(simulatedBox.width);
  const [isSelected, setIsSelected] = useState(false)

  const lockAspectRatio = useMemo(() => simulatedBox.ratio
    ? simulatedBox.ratio
    : defaultImgAspectRatio, [simulatedBox.ratio])

  useOutsideClickHandler(ref, () => {
    if (isSelected) {
      setIsSelected(false)
    }
  });

  const updateImage = useCallback((newImageBox) => {
    const contentState = editorState.getCurrentContent();
    contentState.replaceEntityData(entityKey, { box: newImageBox, menuAncestorType, menuAncestorIds })
    setIsSelected(false)
  }, [editorState, entityKey, menuAncestorType, menuAncestorIds])

  const handleResize = (args) => {
    const x = args[0].movementX, y = args[0].movementY;
    const resizeMove = rotatedBoxDelta({ rotation: 0, direction: args[1], x, y });
    const ratioMove = getRatioMove(resizeMove, lockAspectRatio);
    const { width: newWidth } = reinitializeBoxWidthAndHeight({
      box: {
        ...simulatedBox,
        width: Math.floor(width + ratioMove.width),
        height: 'auto',
      },
      columnWidth: 180,
      onResize: true,
      header,
      footer
    })
    setWidth(newWidth < roundButtonWidth ? roundButtonWidth : newWidth);
  };

  const handleResizeStop = () => {
    updateImage({ ...simulatedBox, width });
  };

  const getPositions = useCallback(() => {
    if (!ref.current) return { top: null, left: null }
    return ref.current.getBoundingClientRect()
  }, [])

  return (
    <>
      <div
        onClick={(e) => {
          setIsSelected(true)
          e.stopPropagation()
        }}
        ref={ref}
      >
        <Resizable
          enable={{
            right: true,
          }}
          lockAspectRatio={lockAspectRatio}
          size={{ width, height: 'auto' }}
          onResize={(...args) => { handleResize(args) }}
          onResizeStop={handleResizeStop}
          handleStyles={{
            right: { zIndex: 2000 },
          }}
          style={{
            display: 'inline-block'
          }}
          maxWidth={180}
          minWidth={roundButtonWidth}
        >
          <div style={{
            visibility: (fromPdf || fromViewer === viewerTypes.PREVIEW) &&
              !isAnUrl(simulatedBox?.content?.src) ? 'hidden' : 'visible',
          }}>
            <Image box={{ ...simulatedBox, width }} updateImage={updateImage} />
          </div>
        </Resizable>
      </div>
      {
        isSelected &&
        ReactDOM.createPortal(
          <FloatyImageMenu
            positions={getPositions()}
            simulatedBox={simulatedBox}
            updateImage={updateImage}
          />,
          document.getElementById('MenuBarPortal')
        )
      }
    </>
  );
}

const ImageDecorator = (props) => {
  const { setEditorState, editorState, readOnly } = useEditor();
  const { box: simulatedBox, menuAncestorType, menuAncestorIds } =
    props.contentState.getEntity(props.entityKey).getData();
  const fromPdf = useStore(({ fromPdf }) => fromPdf);
  const { fromViewer } = usePreviewContext();

  return (
    <span className='image-decorator' data-offset-key={props.offsetKey}>
      {/* Image non éditable */}
      <span contentEditable={false}>
        {setEditorState && !readOnly ? (
          <ImageUpdater
            simulatedBox={simulatedBox}
            entityKey={props.entityKey}
            menuAncestorType={menuAncestorType}
            menuAncestorIds={menuAncestorIds}
            editorState={editorState}
          />
        ) : (
          <div
            style={{
              visibility:
                (fromPdf || fromViewer === viewerTypes.PREVIEW) &&
                  !isAnUrl(simulatedBox?.content?.src)
                  ? 'hidden'
                  : 'visible',
            }}
          >
            <Image box={simulatedBox} fromViewer />
          </div>
        )}
      </span>
      {/* Caractère \"porteur\" gardé dans le DOM mais invisible, pour DraftJS */}
      <span style={{ fontSize: 0 }}>{props.children}</span>
    </span>
  );
};

export default ImageDecorator
